import React, { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import _ from 'lodash';

import { Form } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { ICoHost, ICoHostForm } from 'api/co-hosts';

import { Typography, Checkbox, Modal } from 'ui-kit';
import InputPhoneIntl from 'components/Form/InputPhoneIntl';
import NotificationChannels from 'components/Form/NotificationChannels';
import { Input } from 'components/Form';

import {
  formatPhoneNumber,
  getPhoneNumberCountry,
  isSmsSupportedCountry,
  isValidIntlPhoneNumber,
} from 'components/Form/InputPhoneMask/utils';

import { notificationChannelToFlags } from 'utils/notificationChannel';

import './CoHostModal.scss';

interface ICoHostModal {
  isOpen: boolean;
  onConfirm: ({ data }: { data: ICoHostForm }) => void;
  onCancel: () => void;
  initialData?: ICoHost;
}

const validCoHostSchema = yup.object({
  email: yup.string().email('Please input a valid email.').required('Email address required.'),
  phone_number: yup
    .string()
    .nullable()
    .test(
      'valid-phone-number',
      'Please input a valid phone number.',
      (value) => _.isEmpty(value) || isValidIntlPhoneNumber(value),
    ),
});

const CoHostModal = ({ isOpen, onConfirm, onCancel, initialData }: ICoHostModal) => {
  const { control, getValues, reset, handleSubmit, watch } = useForm<ICoHostForm>({
    mode: 'all',
    defaultValues: {
      id: null,
      email: '',
      phone_number: '',
      is_sms_enabled: true,
      is_whatsapp_enabled: false,
      is_request_sms_enabled: false,
      is_sales_sms_enabled: false,
      is_marketing_sms_enabled: false,
    },
    resolver: yupResolver(validCoHostSchema),
  });

  const smsSupported = isSmsSupportedCountry(getPhoneNumberCountry(watch('phone_number')));

  useEffect(() => {
    if (!isOpen) {
      reset();
    } else if (isOpen && initialData) {
      const initialSmsSupported = isSmsSupportedCountry(
        getPhoneNumberCountry(initialData.phone_number),
      );
      const { isSmsEnabled, isWhatsappEnabled } = notificationChannelToFlags(
        initialData.notification_channel,
      );

      reset({
        ...initialData,
        is_sms_enabled: initialSmsSupported && isSmsEnabled,
        is_whatsapp_enabled: isWhatsappEnabled || (!initialSmsSupported && isSmsEnabled),
      });
    } else {
      reset({
        id: null,
        email: '',
        phone_number: '',
        is_sms_enabled: true,
        is_whatsapp_enabled: false,
        is_request_sms_enabled: false,
        is_sales_sms_enabled: false,
        is_marketing_sms_enabled: false,
      });
    }
  }, [isOpen, reset]);

  const modalTitle = useMemo(() => {
    return initialData ? 'Edit Co-host Contact' : 'Invite Co-host';
  }, [initialData]);

  const handleConfirm = useCallback(() => {
    const data = getValues();

    const phone = formatPhoneNumber({ phone: data.phone_number });

    const formattedData = {
      ...data,
      phone_number: phone,
      is_sms_enabled: !_.isNil(phone) && data.is_sms_enabled,
      is_whatsapp_enabled: !_.isNil(phone) && data.is_whatsapp_enabled,
    };

    onConfirm({ data: formattedData });
  }, [onConfirm, getValues]);

  const handleCancel = useCallback(() => {
    onCancel();
    reset();
  }, [onCancel, reset]);

  return (
    <Modal
      className="CoHostModal"
      visible={isOpen}
      onOk={handleSubmit(handleConfirm)}
      onCancel={handleCancel}
      okText="Save"
      cancelText="Cancel"
      okType="primary"
      destroyOnClose
      okButtonProps={{
        htmlType: 'submit',
      }}
    >
      <Typography className="CoHostModal_title" variant="h6">
        {modalTitle}
      </Typography>

      <Form layout="vertical">
        <Controller
          name="id"
          control={control}
          render={({ field: { value } }) => <input type="hidden" value={value} />}
        />
        <Controller
          name="email"
          control={control}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <Form.Item
              required
              name={name}
              label="Email address"
              className="email-label"
              tooltip={{
                title: 'Emails include request and approve, sales, service info, and reminders.',
                icon: <ExclamationCircleOutlined />,
              }}
              validateStatus={error ? 'error' : ''}
              help={error?.message}
              initialValue={value}
            >
              <Input
                className=""
                defaultValue={value}
                onChange={onChange}
                placeholder="email address..."
                disabled={initialData}
              />
            </Form.Item>
          )}
        />

        <Controller
          name="phone_number"
          control={control}
          render={({ field: { name, value, onChange, onBlur }, fieldState: { error } }) => (
            <Form.Item
              name={name}
              label="Phone number"
              className="phone-number-label"
              validateStatus={error ? 'error' : ''}
              help={error ? error?.message : null}
              initialValue={value}
            >
              <InputPhoneIntl onBlur={onBlur} onChange={onChange} value={value} />
            </Form.Item>
          )}
        />
        <Controller
          name="is_sms_enabled"
          control={control}
          render={({ field: { value: smsEnabled, onChange: onSmsChange } }) => (
            <Controller
              name="is_whatsapp_enabled"
              control={control}
              render={({ field: { value: whatsappEnabled, onChange: onWhatsappChange } }) => (
                <NotificationChannels
                  smsEnabled={smsEnabled}
                  whatsappEnabled={whatsappEnabled}
                  smsSupported={smsSupported}
                  onChange={(next) => {
                    onSmsChange(next.is_sms_enabled);
                    onWhatsappChange(next.is_whatsapp_enabled);
                  }}
                />
              )}
            />
          )}
        />
        <div className="CoHostModal_list_notifications">
          <p className="label-title">Enable notifications for:</p>
          <Controller
            name="is_request_sms_enabled"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <Form.Item name={name}>
                <Checkbox onChange={onChange} checked={value}>
                  Request and Approve: Request, Approve, Deny, Cancelled, Expired
                </Checkbox>
              </Form.Item>
            )}
          />
          <Controller
            name="is_sales_sms_enabled"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <Form.Item name={name}>
                <Checkbox onChange={onChange} checked={value}>
                  Sales: Purchase, Cancellation, Service Reminders, Inventory
                </Checkbox>
              </Form.Item>
            )}
          />
          <Controller
            name="is_marketing_sms_enabled"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <Form.Item name={name}>
                <Checkbox onChange={onChange} checked={value}>
                  News and Feature Updates
                </Checkbox>
              </Form.Item>
            )}
          />
        </div>
      </Form>
    </Modal>
  );
};
export default CoHostModal;
